'use client';

import { noop } from '@capsicum/common';
import { createContext, useContext } from 'react';

import { type GenericDrawerProps } from '../../ui/molecules/generic-drawer/GenericDrawer';

type RequestPermissionDrawerProps = Omit<
	Partial<GenericDrawerProps>,
	'onButtonPress' | 'onSecondaryActionOnButtonPress'
> & { onDismiss?: () => void };

export type RequestPermissionFn = (props?: RequestPermissionDrawerProps) => void;

export enum PermissionStatusEnum {
	DEFAULT = 'default',
	DENIED = 'denied',
	GRANTED = 'granted'
}

export type WebPushContextShape = {
	canRequestPermission: boolean;
	displayEnablePushNotificationsBySettingsModal: () => void;
	isPermissionDenied: boolean;
	isPermissionGranted: boolean;
	isSupported: boolean;
	requestInitialPermission: RequestPermissionFn;
	requestNotificationsPermission: () => void;
	requestPermission: RequestPermissionFn;
	requestPermissionAfterAction: RequestPermissionFn;
};

export const DEFAULT_WEBPUSH_CONTEXT_VALUE: WebPushContextShape = {
	canRequestPermission: false,
	displayEnablePushNotificationsBySettingsModal: noop,
	isPermissionDenied: false,
	isPermissionGranted: false,
	isSupported: false,
	requestInitialPermission: noop,
	requestNotificationsPermission: noop,
	requestPermission: noop,
	requestPermissionAfterAction: noop
};

export const WebPushContext = createContext<null | WebPushContextShape>(null);

export const useWebPush = () => {
	const context = useContext(WebPushContext);

	if (!context) {
		throw new Error(`useWebPush cannot be used outside the WebPushProvider`);
	}

	return context;
};
